import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  MessageCircle, Send, Loader2, CheckCircle2, XCircle, RefreshCw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_COLORS = {
  facebook: { bg: '#1877F2', label: 'Facebook' },
  instagram: { bg: '#E4405F', label: 'Instagram' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function EngagementTestPage() {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [platform, setPlatform] = useState('facebook');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [s, p] = await Promise.all([
          api('/api/engagement-test/status'),
          api('/api/engagement-test/pages'),
        ]);
        setStatus(s);
        setPages(p);
        if (p.length > 0) setSelectedPage(p[0].page_id);
      } catch (err) {
        toast.error('Failed to check access');
      }
    };
    fetchStatus();
  }, []);

  const fetchComments = async () => {
    if (!selectedPage) return;
    setLoading(true);
    try {
      const data = await api(`/api/engagement-test/comments/${selectedPage}?platform=${platform}`);
      setComments(data.comments || []);
      if (data.comments?.length === 0) {
        toast.info('No comments found for this page/platform');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api('/api/engagement-test/reply', {
        method: 'POST',
        body: JSON.stringify({
          comment_id: commentId,
          message: replyText,
          page_id: selectedPage,
          platform: platform,
        }),
      });
      toast.success('Reply sent!');
      setReplyingTo(null);
      setReplyText('');
      fetchComments(); // Refresh
    } catch (err) {
      toast.error('Reply failed: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Engagement Test
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Test native Facebook &amp; Instagram comment reading and replying
          </p>
        </div>
      </div>

      {/* Connection Status */}
      <Card className="mb-6">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Connection Status</h3>
          {status ? (
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                {status.has_access ? (
                  <CheckCircle2 size={18} className="text-emerald-600" />
                ) : (
                  <XCircle size={18} className="text-red-500" />
                )}
                <span className="text-sm font-medium">
                  {status.has_access ? 'Connected' : 'No page access'}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold" style={{ color: '#1877F2' }}>{status.fb_pages}</span> FB Pages
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold" style={{ color: '#E4405F' }}>{status.ig_accounts}</span> IG Accounts
              </div>
              {status.page_names?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Pages: {status.page_names.slice(0, 3).join(', ')}{status.page_names.length > 3 ? ` +${status.page_names.length - 3} more` : ''}
                </div>
              )}
            </div>
          ) : (
            <Loader2 className="animate-spin" size={16} />
          )}
          {status && !status.has_access && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <p className="font-semibold mb-1">No pages connected</p>
                  <p>Your Facebook token doesn't have access to any pages. When generating the token, you need to select which pages to grant access to. Re-generate the token and select all your client pages.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page & Platform selector */}
      {pages.length > 0 && (
        <Card className="mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Select value={selectedPage} onValueChange={setSelectedPage}>
              <SelectTrigger className="w-[250px]"><SelectValue placeholder="Select a page" /></SelectTrigger>
              <SelectContent>
                {pages.map(p => (
                  <SelectItem key={p.page_id} value={p.page_id}>
                    {p.name} {p.ig_account_id ? '(FB + IG)' : '(FB)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchComments} disabled={loading || !selectedPage} style={{ background: '#1f2a4d' }}>
              {loading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
              Load Comments
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Comments */}
      <Card>
        <CardContent className="p-0">
          {comments.length === 0 ? (
            <div className="py-16 text-center">
              <MessageCircle size={32} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                {pages.length === 0 ? 'No pages connected — re-generate token with page access' : 'Select a page and click "Load Comments"'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {comments.map((comment) => {
                const pmeta = PLATFORM_COLORS[comment.platform] || { bg: '#666', label: comment.platform };
                return (
                  <div key={comment.id} className="px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: pmeta.bg }}>
                        {pmeta.label[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold">{comment.author}</span>
                          <span className="text-[10px] text-muted-foreground">{timeAgo(comment.created_time)}</span>
                          {comment.post_preview && (
                            <span className="text-[10px] text-muted-foreground">on: "{comment.post_preview.substring(0, 40)}..."</span>
                          )}
                        </div>
                        <p className="text-sm">{comment.text}</p>

                        {/* Reply section */}
                        {replyingTo === comment.id ? (
                          <div className="mt-3 flex items-center gap-2">
                            <Input
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder="Type your reply..."
                              className="flex-1 text-sm"
                              autoFocus
                              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(comment.id); }}}
                            />
                            <Button size="sm" onClick={() => handleReply(comment.id)} disabled={sending || !replyText.trim()} style={{ background: '#1f2a4d' }}>
                              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="mt-1 text-xs h-7 text-muted-foreground" onClick={() => setReplyingTo(comment.id)}>
                            <MessageCircle size={12} className="mr-1" /> Reply
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
